import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import { type GraphQLConnection } from "@/lib/graphql-pagination";

// --- Types ---

export interface PromiseToPayRow {
  promiseId: number;
  customerName: string;
  customerAccountNumber: string;
  statusCode: string;
  statusName: string;
  assignedUserName: string;
  promiseDate: string;
  promisedAmount: number;
  daysOverdue: number | null;
}

export interface PromisesToPayTableGroup {
  status: string;
  rows: PromiseToPayRow[];
}

// --- GraphQL ---

interface PromiseToPayTableNode {
  promiseId: number;
  customerName: string | null;
  customerAccountNumber: string | null;
  statusCode: string | null;
  statusName: string | null;
  assignedUserName: string | null;
  promiseDate: string;
  promisedAmount: number;
  daysOverdue: number | null;
}

interface PromiseToPayTableListResponse {
  promisesToPay: {
    promiseToPayList: GraphQLConnection<PromiseToPayTableNode>;
  };
}

const PROMISES_TABLE_QUERY = `
  query PromisesToPayTable($first: Int, $after: String, $filter: PromiseToPayListFilterInput) {
    promisesToPay {
      promiseToPayList(first: $first, after: $after, filter: $filter) {
        edges {
          node {
            promiseId
            customerName
            customerAccountNumber
            statusCode
            statusName
            assignedUserName
            promiseDate
            promisedAmount
            daysOverdue
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// --- Mock data ---

function generateMockTableData(): PromisesToPayTableGroup[] {
  return [
    {
      status: "Broken",
      rows: [
        { promiseId: 1, customerName: "Summit Distribution", customerAccountNumber: "ACC-010", statusCode: "Broken", statusName: "Broken", assignedUserName: "Sarah Johnson", promiseDate: "2026-03-23", promisedAmount: 14921, daysOverdue: 3 },
        { promiseId: 2, customerName: "Nexus Group", customerAccountNumber: "ACC-019", statusCode: "Broken", statusName: "Broken", assignedUserName: "Marcus Clarke", promiseDate: "2026-03-25", promisedAmount: 31800, daysOverdue: 1 },
        { promiseId: 3, customerName: "Delta Corp", customerAccountNumber: "ACC-024", statusCode: "Broken", statusName: "Broken", assignedUserName: "Emily Rodriguez", promiseDate: "2026-03-24", promisedAmount: 8100, daysOverdue: 2 },
      ],
    },
    {
      status: "Active",
      rows: [
        { promiseId: 4, customerName: "Horizon Retail", customerAccountNumber: "ACC-013", statusCode: "Active", statusName: "Active", assignedUserName: "Sarah Johnson", promiseDate: "2026-03-26", promisedAmount: 22000, daysOverdue: 0 },
        { promiseId: 5, customerName: "Apex Logistics", customerAccountNumber: "ACC-015", statusCode: "Active", statusName: "Active", assignedUserName: "Emily Rodriguez", promiseDate: "2026-03-26", promisedAmount: 9750, daysOverdue: 0 },
      ],
    },
  ];
}

// --- Hook ---

export function usePromisesToPayTableGraphQL(
  selectedUserId?: string,
  dateFrom?: Date,
  dateTo?: Date,
  currencyCode?: string,
) {
  const apiConfig = useApiConfig();
  const mockData = generateMockTableData();

  return useModeAwareQuery<PromisesToPayTableGroup[], Error>(
    ["promises-to-pay-table", selectedUserId, dateFrom?.toISOString(), dateTo?.toISOString(), currencyCode],
    async ({ signal }) => {
      const filter: Record<string, unknown> = {
        statusCode: { containsValues: ["Active", "Broken"] },
      };

      if (selectedUserId && selectedUserId !== "everyone") {
        filter.assignedUserId = { eq: Number(selectedUserId) };
      }

      if (currencyCode) {
        filter.currencyCode = { eq: currencyCode };
      }

      if (dateFrom || dateTo) {
        const dateFilter: Record<string, string> = {};
        if (dateFrom) {
          const from = new Date(dateFrom);
          from.setHours(0, 0, 0, 0);
          dateFilter.ge = from.toISOString();
        }
        if (dateTo) {
          const to = new Date(dateTo);
          to.setHours(23, 59, 59, 999);
          dateFilter.le = to.toISOString();
        }
        filter.createdAt = dateFilter;
      }

      // Fetch all matching promises (up to 500)
      let allNodes: PromiseToPayTableNode[] = [];
      let hasMore = true;
      let after: string | null = null;

      while (hasMore) {
        const variables: Record<string, unknown> = { first: 500, filter };
        if (after) variables.after = after;

        const response = await graphqlQuery<PromiseToPayTableListResponse>(
          apiConfig,
          PROMISES_TABLE_QUERY,
          variables,
          signal,
        );

        const connection = response.promisesToPay.promiseToPayList;
        allNodes = [...allNodes, ...connection.edges.map(x => x.node)];
        hasMore = connection.pageInfo.hasNextPage;
        after = connection.pageInfo.endCursor;

        if (allNodes.length >= 2000) break;
      }

      // Map to rows
      const rows: PromiseToPayRow[] = allNodes.map(x => ({
        promiseId: x.promiseId,
        customerName: x.customerName ?? "Unknown",
        customerAccountNumber: x.customerAccountNumber ?? "",
        statusCode: x.statusCode ?? "Unknown",
        statusName: x.statusName ?? x.statusCode ?? "Unknown",
        assignedUserName: x.assignedUserName ?? "",
        promiseDate: x.promiseDate,
        promisedAmount: x.promisedAmount,
        daysOverdue: x.daysOverdue,
      }));

      // Group by status, then sort within each group by customerName
      const groupMap = new Map<string, PromiseToPayRow[]>();

      // Ensure Broken comes first, then Active
      const statusOrder = ["Broken", "Active"];
      for (const status of statusOrder) {
        groupMap.set(status, []);
      }

      for (const row of rows) {
        const status = row.statusName;
        const existing = groupMap.get(status) ?? [];
        existing.push(row);
        groupMap.set(status, existing);
      }

      const groups: PromisesToPayTableGroup[] = [];
      for (const [status, statusRows] of groupMap.entries()) {
        if (statusRows.length === 0) continue;
        statusRows.sort((a, b) => a.customerName.localeCompare(b.customerName));
        groups.push({ status, rows: statusRows });
      }

      return groups;
    },
    mockData,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    },
  );
}
